/**
 * Normalize barcode input for storage.
 *
 * Supported input lengths:
 *  8  → UPC-E expansion to 12-digit UPC-A (recalculates check digit)
 *  10 → pad two leading zeros (legacy behavior)
 *  11 → 11-digit base without check digit → append GS1 mod-10 check digit (12-digit)
 *  12 → stored as-is (no conversion)
 *  13 → stored as-is (no conversion)
 *  14 → stored as-is (no conversion)
 *
 * Returns the normalized string on success, or null for invalid input.
 */

const calcUpcACheckDigit = (elevenDigits) => {
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const digit = parseInt(elevenDigits[i], 10);
    sum += (i % 2 === 0) ? digit * 3 : digit;
  }
  return String((10 - (sum % 10)) % 10);
};

const buildGtin12FromBase = (base11) => base11 + calcUpcACheckDigit(base11);

const expandUpcE = (upc) => {
  const S = upc[0], a = upc[1], b = upc[2], c = upc[3];
  const d = upc[4], e = upc[5], N = upc[6];

  let expanded;
  if (["0", "1", "2"].includes(N)) expanded = `${S}${a}${b}${N}0000${c}${d}${e}`;
  else if (N === "3")              expanded = `${S}${a}${b}${c}00000${d}${e}`;
  else if (N === "4")              expanded = `${S}${a}${b}${c}${d}00000${e}`;
  else                             expanded = `${S}${a}${b}${c}${d}${e}0000${N}`;

  return buildGtin12FromBase(expanded);
};

const normalizeUpc = (raw) => {
  if (!raw) return null;
  const upc = String(raw).replace(/\s/g, "");
  if (!/^\d+$/.test(upc)) return null;

  const len = upc.length;

  if (len === 14 || len === 13 || len === 12) return upc;
  if (len === 11) return buildGtin12FromBase(upc);
  if (len === 10) return "00" + upc;
  if (len === 8)  return expandUpcE(upc);

  return null;
};

export const getNormalizeUpcError = (raw) => {
  if (!raw) return "Barcode is required";
  const upc = String(raw).replace(/\s/g, "");
  if (!/^\d+$/.test(upc)) return "Invalid barcode. Only numbers are allowed.";
  return `Invalid barcode length (${upc.length} digits). Accepted: 8, 10, 11, 12, 13, or 14 digits.`;
};

export default normalizeUpc;
